import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Expense {
  id: string
  employee_name: string
  category_name: string
  description: string
  amount: number
  currency: string
  expense_date: string
  status: string
  merchant: string
}

export default function ExpenseManagementPage() {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [expensesRes, catsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/expenses/'),
      fetch(API_BASE + '/expenses/categories'),
      fetch(API_BASE + '/expenses/statistics')
    ])
    if (expensesRes.ok) setExpenses((await expensesRes.json()).expenses || [])
    if (catsRes.ok) setCategories((await catsRes.json()).categories || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reimbursed': return 'bg-green-100 text-green-800'
      case 'approved': return 'bg-blue-100 text-blue-800'
      case 'pending_approval': return 'bg-yellow-100 text-yellow-800'
      case 'submitted': return 'bg-indigo-100 text-indigo-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Expense Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Submit Expense</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Expenses</div>
              <div className="text-2xl font-bold">{statistics.total_expenses}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Approval</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_approval}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Submitted</div>
              <div className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_submitted_amount)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Reimbursed</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_reimbursed_amount)}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Expenses</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {expenses.slice(0, 12).map(expense => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{expense.employee_name}</div>
                      <div className="text-xs text-gray-500">{expense.expense_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{expense.category_name}</div>
                      <div className="text-xs text-gray-500">{expense.merchant}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold">{formatCurrency(expense.amount, expense.currency)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(expense.status)}`}>
                        {expense.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Categories</h3>
            <div className="space-y-3">
              {categories.map(cat => (
                <div key={cat.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-medium text-sm">{cat.name}</span>
                    <span className="text-xs text-gray-500">Limit: {formatCurrency(cat.monthly_limit)}</span>
                  </div>
                  {cat.requires_approval && <div className="text-xs text-yellow-600">Requires approval</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
